// Competitive Intelligence — the complete CI API surface.
//
// Moved here from src/api/auth.js (2026-10-07) at Sheane's direction so all CI
// RPCs live in one module — the same per-feature module split Voice Intelligence
// (src/api/conversation.js), Local Intelligence, and Search Console already use.
// auth.js is no longer touched by CI work. src/api/index.js re-exports this
// module, so every existing `import { getCiSummary, ... } from "../../api"`
// call site keeps working unchanged.
//
// Behaviour is byte-identical to the old auth.js section: same legacy
// `{ error, data, message }` envelope (wrapSupabase), same labelled error
// throws (wrapSupabaseError), same ciRpc / ciReportRpc dispatchers, same
// per-helper comments. Nothing was "improved" in the move — if you're reading
// a comment that seems stale, it survived the move deliberately.
//
// Original migration note (2026-07-03): 38 CI RPCs deployed silently between
// 2026-06-23 and 2026-06-26 (backend never sent an update doc). Signatures
// discovered by enumerating /rest/v1/ OpenAPI spec — the audit's
// prefix-guessing had missed the actual `ci_get_*` / `ci_report_*` naming.
//
// CI is forward-looking — no date range params. Reports mostly take
// (p_client_id) or (p_category_code, p_client_id); a few take extras
// (p_check_in, p_days_forward, p_threshold_pct, p_limit) — see per-helper
// signatures matching the OpenAPI spec exactly.

import { supabase } from '../lib/supabase';

// ─── Envelope helpers ────────────────────────────────────────────────────────
// Copied verbatim from auth.js (they were module-private there). Wraps Supabase
// RPC responses in the legacy `{ data, error, message }` envelope the Express
// API returned so call sites like `setKpis(resp?.data ?? null)` keep working.
const wrapSupabase = (data) => ({ error: false, data, message: 'ok' });
// `label` is an optional human-readable descriptor of the failing call (the RPC
// name, or a phrase like 'playbook detail'). Without it every failure surfaced
// as a bare "Supabase RPC failed" with no clue which call broke.
const wrapSupabaseError = (err, label) => {
  const detail = err?.message || 'Supabase RPC failed';
  const message = label ? `${label}: ${detail}` : detail;
  const error = new Error(message);
  error.response = { data: { error: true, message, data: null } };
  throw error;
};

async function ciRpc(name, params, label) {
  const { data, error } = await supabase.rpc(name, params);
  if (error) wrapSupabaseError(error, label);
  return wrapSupabase(data);
}

// Sentinel-capable reports (Velocity, VsBaseline, DemandIndex) return
// { status, message, current_sync, results } instead of a plain array.
// For those we normalize to { items, status, current_sync, message } so
// panels can read data.status + data.items uniformly.
//
// For every other report the RPC returns a plain array. We identity-pass
// that — the many consumers that do `Array.isArray(data) ? data : []`
// (CompetitiveRanking, Revenue, PriceDistribution, BookingWindow, etc.)
// need the raw array shape, and the paginated consumers reach through
// `usePaginatedCache` which now tolerates both bare-array and {items}
// shapes.
async function ciReportRpc(name, params, label) {
  const { data, error } = await supabase.rpc(name, params);
  if (error) wrapSupabaseError(error, label);
  if (Array.isArray(data)) return wrapSupabase(data);
  if (data && typeof data === 'object') {
    const items = Array.isArray(data.items)
      ? data.items
      : (Array.isArray(data.results) ? data.results : []);
    return wrapSupabase({
      ...data,
      items,
      status: data.status ?? null,
      current_sync: data.current_sync ?? null,
      message: data.message ?? null,
    });
  }
  return wrapSupabase([]);
}

export const getCiSummary = (clientId) =>
  ciRpc('ci_get_summary', { p_client_id: clientId }, 'competitive summary');

export const getCiAvailableFilters = (clientId) =>
  ciRpc('ci_get_available_filters', { p_client_id: clientId }, 'competitive filters');

export const getCiRateCalendar = async (clientId, { categoryCode = null, normalizedGroup = null, tier = null, startDate, endDate, page, limit } = {}) => {
  // RPC does not accept startDate/endDate/page/limit — the calendar returns a
  // fixed forward-looking window server-side. Preserved on the JS signature
  // for backward compat; silently dropped.
  void startDate; void endDate; void page; void limit;
  // 2026-07-16 — RPC returns a bare array of rows. Page code (CompetitiveIntelligencePage)
  // and RateCalendarPanel both expect the legacy Cloud Run envelope
  // `{items, pagination:{total_pages}}`. Wrap here so consumers Just Work.
  const resp = await ciRpc('ci_get_rate_calendar', {
    p_client_id: clientId,
    p_category_code: categoryCode,
    p_normalized_group: normalizedGroup,
    p_tier: tier,
  }, 'rate calendar');
  const rows = Array.isArray(resp?.data) ? resp.data : (resp?.data?.items ?? []);
  return {
    ...resp,
    data: {
      items: rows,
      pagination: { page: 1, limit: rows.length, total_items: rows.length, total_pages: 1 },
    },
  };
};

export const getCiRateDetail = (clientId, { checkIn, categoryCode, tier = null } = {}) =>
  ciRpc('ci_get_rate_detail', {
    p_client_id: clientId,
    p_check_in: checkIn,
    p_category_code: categoryCode,
    p_tier: tier,
  }, 'rate detail');

export const getCiMarketPosition = (clientId, { checkIn, categoryCode, tier = null } = {}) =>
  ciRpc('ci_get_market_position', {
    p_client_id: clientId,
    p_check_in: checkIn,
    p_category_code: categoryCode,
    p_tier: tier,
  }, 'market position');

export const getCiActions = (clientId) =>
  ciRpc('ci_get_actions', { p_client_id: clientId }, 'AI pricing actions');

export const dismissCiAction = (clientId, actionId) =>
  ciRpc('ci_dismiss_action', { p_client_id: clientId, p_action_id: actionId }, 'dismiss action');

export const getCiCompetitors = (clientId) =>
  ciRpc('ci_get_competitors', { p_client_id: clientId }, 'competitors');

// ─── CI — Competitor self-serve selection (spec §5.3 / to-do B5) ──────────
// `ci_search_competitors` is a READ RPC: it returns { results, result_count,
// no_results, facets, config } and rejects (via ciRpc → wrapSupabaseError) on a
// Postgres-level error. `ci_add_competitor` / `ci_remove_competitor` are WRITE
// RPCs: they resolve with { success, error, message, ...extras } inside `data`,
// so callers must check `data.success` for user-facing failures (C4 two-layer
// error handling). `null` params mean "no filter" — the backend treats them as
// "all", which is what we want for the show-everything-by-default rule (B5 §3).
export const searchCiCompetitors = (clientId, {
  search = null,
  maxDistanceMiles = null,
  pricingOnly = null,
  propertyTypes = null,
  ownershipClasses = null,
  categories = null,
} = {}) =>
  ciRpc('ci_search_competitors', {
    p_client_id: clientId,
    p_search: search && search.trim() ? search.trim() : null,
    p_max_distance_miles: maxDistanceMiles,
    p_pricing_only: pricingOnly == null ? null : !!pricingOnly,
    p_property_types: propertyTypes && propertyTypes.length ? propertyTypes : null,
    p_ownership_classes: ownershipClasses && ownershipClasses.length ? ownershipClasses : null,
    p_categories: categories && categories.length ? categories : null,
  }, 'competitor search');

export const addCiCompetitor = (clientId, { atlasParkId, actor = 'client' } = {}) =>
  ciRpc('ci_add_competitor', {
    p_client_id: clientId,
    p_atlas_park_id: atlasParkId,
    p_actor: actor,
  }, 'add competitor');

export const removeCiCompetitor = (clientId, { atlasParkId, actor = 'client', reason = null } = {}) =>
  ciRpc('ci_remove_competitor', {
    p_client_id: clientId,
    p_atlas_park_id: atlasParkId,
    p_actor: actor,
    p_reason: reason,
  }, 'remove competitor');

// `ci_get_competitor_settings` is a READ RPC: returns { plan, upgrade_options,
// competitors, recent_changes, ... } describing the client's plan limits and
// current roster. Rejects on a Postgres-level error (C4).
export const getCiCompetitorSettings = (clientId) =>
  ciRpc('ci_get_competitor_settings', { p_client_id: clientId }, 'competitor settings');

// `ci_request_competitor` is a WRITE RPC: resolves with { success, error,
// message } inside `data`. A Postgres-level error rejects; a user-facing
// failure (e.g. duplicate request) arrives as data.success === false with
// data.error rendered verbatim (C4 / B5 §6).
export const requestCiCompetitor = (clientId, {
  requestedName,
  city = null,
  state = null,
  website = null,
  searchTerm = null,
} = {}) =>
  ciRpc('ci_request_competitor', {
    p_client_id: clientId,
    p_requested_name: requestedName,
    p_city: city,
    p_state: state,
    p_website: website,
    p_search_term: searchTerm,
  }, 'request competitor');

export const getCiRateChanges = (clientId, { limit = null, offset = null, categoryCode = null, direction = null, unreadOnly = null, competitorAtlasParkId = null } = {}) =>
  ciRpc('ci_get_rate_changes', {
    p_client_id: clientId,
    p_category_code: categoryCode,
    p_competitor_atlas_park_id: competitorAtlasParkId,
    p_direction: direction,
    p_limit: limit,
    p_offset: offset,
    // Item 2 (work-page-fixes) — the spec's canonical feed fetch sends
    // p_unread_only:false to return read + unread rows. Previously this mapped
    // falsy → null, and the backend dropped read rows on null (items vanished
    // after a refresh). Now: explicit true → unread only; false/null → show all.
    p_unread_only: unreadOnly === true ? true : false,
  }, 'rate changes');

// Pass null/empty changeIds to mark all unread rows for the client as read.
export const markCiRateChangesRead = (clientId, changeIds = null) =>
  ciRpc('ci_mark_changes_read', {
    p_client_id: clientId,
    p_change_ids: changeIds && changeIds.length ? changeIds : null,
  }, 'mark rate changes read');

// ─── Competitive Intelligence — Section 8 Analysis Reports ──────────────
// Most reports take (p_client_id) or (p_category_code, p_client_id). RPCs do
// not accept pagination (p_page/p_limit) — CI RPCs return the full set
// server-side; slice on the client if needed. Signatures below match the
// exact OpenAPI spec at /rest/v1/ enumeration.

export const getCiReportDemandSignals = (clientId, { page, limit } = {}) => {
  void page; void limit;
  return ciReportRpc('ci_report_demand_signals', { p_client_id: clientId }, 'demand signals');
};

export const getCiReportPriceGaps = (clientId, { categoryCode = null, page, limit } = {}) => {
  void page; void limit;
  return ciReportRpc('ci_report_price_gaps', { p_client_id: clientId, p_category_code: categoryCode }, 'price gaps');
};

export const getCiReportWeekdayWeekend = (clientId, { categoryCode = null } = {}) =>
  ciReportRpc('ci_report_weekday_weekend', { p_client_id: clientId, p_category_code: categoryCode }, 'weekday/weekend report');

export const getCiReportBookingWindow = (clientId, { categoryCode = null } = {}) =>
  ciReportRpc('ci_report_booking_window', { p_client_id: clientId, p_category_code: categoryCode }, 'booking window');

export const getCiReportEventSpikes = (clientId, { categoryCode = null, thresholdPct = 10 } = {}) =>
  ciReportRpc('ci_report_event_spikes', { p_client_id: clientId, p_category_code: categoryCode, p_threshold_pct: thresholdPct }, 'event spikes');

export const getCiReportSiteComparison = (clientId, { categoryCode = null, page, limit } = {}) => {
  void page; void limit;
  return ciReportRpc('ci_report_site_comparison', { p_client_id: clientId, p_category_code: categoryCode }, 'site comparison');
};

export const getCiReportAvailabilityTrends = (clientId) =>
  ciReportRpc('ci_report_availability_trends', { p_client_id: clientId }, 'availability trends');

export const getCiReportPriceDistribution = (clientId, { categoryCode = null } = {}) =>
  ciReportRpc('ci_report_price_distribution', { p_client_id: clientId, p_category_code: categoryCode }, 'price distribution');

export const getCiReportRevenueOpportunity = (clientId, { categoryCode = null } = {}) =>
  ciReportRpc('ci_report_revenue_opportunity', { p_client_id: clientId, p_category_code: categoryCode }, 'revenue opportunity');

export const getCiReportHighValueDates = (clientId, { limit = 20 } = {}) =>
  ciReportRpc('ci_report_high_value_dates', { p_client_id: clientId, p_limit: limit }, 'high value dates');

export const getCiReportDynamicPricing = (clientId, { categoryCode = null, page, limit } = {}) => {
  void page; void limit;
  return ciReportRpc('ci_report_dynamic_pricing', { p_client_id: clientId, p_category_code: categoryCode }, 'dynamic pricing');
};

export const getCiReportCompetitiveRanking = (clientId, { categoryCode = null } = {}) =>
  ciReportRpc('ci_report_competitive_ranking', { p_client_id: clientId, p_category_code: categoryCode }, 'competitive ranking');

export const getCiReportInventoryConcentration = (clientId) =>
  ciReportRpc('ci_report_inventory_concentration', { p_client_id: clientId }, 'inventory concentration');

export const getCiReportCompetitorVulnerability = (clientId) =>
  ciReportRpc('ci_report_competitor_vulnerability', { p_client_id: clientId }, 'competitor vulnerability');

export const getCiReportRateIntegrity = (clientId, { categoryCode = null } = {}) =>
  ciReportRpc('ci_report_rate_integrity', { p_client_id: clientId, p_category_code: categoryCode }, 'rate integrity');

export const getCiReportDowHeatmap = (clientId, { categoryCode = null } = {}) =>
  ciReportRpc('ci_report_dow_heatmap', { p_client_id: clientId, p_category_code: categoryCode }, 'DOW heatmap');

export const getCiReportForwardCalendar = (clientId, { categoryCode = null, daysForward = 90, page, limit } = {}) => {
  void page; void limit;
  return ciReportRpc('ci_report_forward_calendar', { p_client_id: clientId, p_category_code: categoryCode, p_days_forward: daysForward }, 'forward calendar');
};

export const getCiReportMarketPositionDetail = (clientId, { categoryCode = null, page, limit } = {}) => {
  // RPC is named `ci_report_market_position` (no `_detail` suffix); frontend
  // keeps the longer name for backward compat with existing consumers.
  void page; void limit;
  return ciReportRpc('ci_report_market_position', { p_client_id: clientId, p_category_code: categoryCode }, 'market position detail');
};

export const getCiReportDowRevenueHeatmap = (clientId, { categoryCode = null } = {}) =>
  ciReportRpc('ci_report_dow_revenue_heatmap', { p_client_id: clientId, p_category_code: categoryCode }, 'DOW revenue heatmap');

export const getCiReportAbsorptionVelocity = (clientId, { categoryCode = null } = {}) =>
  // Sentinel-capable: returns { status: 'needs_multiple_syncs', message, current_sync, results }
  // when the client doesn't have 2+ sync cycles yet. ciReportRpc reshapes to
  // { items, status, current_sync, message } so panels see one contract.
  ciReportRpc('ci_report_absorption_velocity', { p_client_id: clientId, p_category_code: categoryCode }, 'absorption velocity');

export const getCiReportAbsorptionVsBaseline = (clientId, { categoryCode = null } = {}) =>
  // Same sentinel handling as velocity.
  ciReportRpc('ci_report_absorption_vs_baseline', { p_client_id: clientId, p_category_code: categoryCode }, 'absorption vs baseline');

export const getCiReportAbsorptionHistory = (clientId, { checkIn, categoryCode = null } = {}) =>
  ciReportRpc('ci_report_absorption_history', { p_client_id: clientId, p_check_in: checkIn, p_category_code: categoryCode }, 'absorption history');

export const getCiReportMarketAbsorption = (clientId, { checkIn, categoryCode = null } = {}) =>
  ciReportRpc('ci_report_market_absorption', { p_client_id: clientId, p_check_in: checkIn, p_category_code: categoryCode }, 'market absorption');

export const getCiReportDemandIndex = (clientId, { categoryCode = null, page, limit } = {}) => {
  void page; void limit;
  return ciReportRpc('ci_report_demand_index', { p_client_id: clientId, p_category_code: categoryCode }, 'demand index');
};

export const getCiTrends = (clientId, { categoryCode = null, months = null } = {}) =>
  ciRpc('ci_get_trends', { p_client_id: clientId, p_category_code: categoryCode, p_months: months }, 'rate trends');

export const getCiHolidayPremiums = (clientId, { categoryCode = null } = {}) =>
  ciRpc('ci_get_holiday_premiums', { p_client_id: clientId, p_category_code: categoryCode }, 'holiday premiums');

// GCP-8 — 2026-07-07. `ci_get_competitor_detail(p_atlas_park_id,
// p_category_code, p_client_id)` deployed since the Part-3 CI audit
// (verified via OpenAPI enum). The Part-1 "ghost endpoint" no longer
// applies — this is a real, callable RPC.
export const getCiCompetitorDetail = (clientId, { atlasParkId, categoryCode = null } = {}) =>
  ciRpc('ci_get_competitor_detail', {
    p_client_id: clientId,
    p_atlas_park_id: atlasParkId,
    p_category_code: categoryCode,
  }, 'competitor detail');

// ─── CI — New RPCs (Brian's Sept-2026 response, §A4/§A6) ──────────────────
// The two RPCs that post-date the 2026-07-03 migration. Same ciRpc dispatcher
// and envelope as the rest of this module so the page code in Item 2 reads
// them exactly like getCiSummary does (`resp?.data` + `.catch`).

// ci_get_panel_insights — the per-panel sentence layer powering the `say`
// prop (Item 2 PART C: iterate `insights.panels`, match view.category_code
// against the selected tab, exclude confidence === 'insufficient', fold the
// verdict in when non-null). `categoryCode` null/undefined → SQL NULL →
// insights across every category.
export const ciGetPanelInsights = (clientId, categoryCode) =>
  ciRpc('ci_get_panel_insights', { p_client_id: clientId, p_category_code: categoryCode ?? null }, 'panel insights');

// ci_get_absorption_dates — the pickable check-in dates for the absorption
// report panels. Rows carry the date, date_window_name, is_past, and
// syncs_tracked fields AbsorptionDatePicker renders (Item 3). Passing null
// categoryCode asks for dates across every category.
export const ciGetAbsorptionDates = (clientId, categoryCode) =>
  ciRpc('ci_get_absorption_dates', { p_client_id: clientId, p_category_code: categoryCode ?? null }, 'absorption dates');
